import { useEffect, useMemo, useState, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { chevronRight, clock, flashSlash, notificationBell, repair, truck, tickCircleExito } from 'assets/images';
import { TipoEstadoActualSecundarioTrackingCorteEnum, TipoEstadoVistaDashboardEnum } from 'config';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import { RootState } from 'store';
import { datoNoValido } from 'utils/colorBallsUtils';
import { CADENA_VACIA } from 'utils/constants';
import { formatTerDisplay, formatTimeOnly, SUFIJO_HORAS } from 'utils/formatters/dateFormatter';
import { mapApiResponseToDashboardData } from 'utils/outageDataMapper';
import BotonCerrarDashboard from './BotonCerrarDashboard';
import DetalleAvance from './DetalleAvance';
import ModalSuscripcionTer from './ModalSuscripcionTer';
import OutageClientesChart from './OutageClientesChart';
import OutageReportarBox from './OutageReportarBox';
import useStyles from './OutageDashboardStyles';
import {
  anchoBarraProgreso,
  CANTIDAD_ETAPAS_CON_BRIGADA,
  CANTIDAD_ETAPAS_SIN_BRIGADA,
  ESTADO_NODO_ACTIVO,
  ESTADO_NODO_HECHO,
  ESTADO_NODO_PENDIENTE,
  INDICE_BRIGADA_ASIGNADA,
  INDICE_BRIGADA_TERRENO,
  INDICE_CORTE_CONFIRMADO,
  INDICE_REPOSICION,
  PROGRESO_COMPLETO,
  StepKind,
  TEST_ID_TIMELINE_CUATRO,
  TEST_ID_TIMELINE_DOS,
  VARIABLE_ANCHO_PROGRESO,
  TEST_ID_AVANCE,
  TEST_ID_BOTON_NOTIFICAR,
  TEST_ID_BOTON_REPORTAR_DASHBOARD,
  TEST_ID_CHART,
  TEST_ID_DASHBOARD,
  TEST_ID_FOOTER,
  TEST_ID_MOTIVO,
  TEST_ID_REPORTAR,
  TEST_ID_TER_BOX,
  TEST_ID_TER_SUSCRITO,
  TEST_ID_TER_VACIO,
  TEST_ID_TOGGLE_DETALLE,
} from './outageDashboard.constants';
import { buildDatosGraficoClientes, buildFooterTexto, buildTerLabels } from './outageDashboard.helpers';
import useDetalleAvanceToggle from './useDetalleAvanceToggle';

/**
 * Dashboard de corte imprevisto en el mapa.
 * @param data respuesta de tracking del corte
 * @param numeroOrden identificador de la orden
 * @param notificacionTerActivo indica si se puede ofrecer la suscripción al TER
 * @param onDetalleAvanceChange avisa al modal cuando se abre el detalle
 */
const OutageDashboard = ({
  data,
  numeroOrden,
  notificacionTerActivo,
  onDetalleAvanceChange = undefined,
  onCerrar = undefined,
}: {
  data: TrackingCorteEstadosResponseModel;
  numeroOrden: string;
  notificacionTerActivo: boolean;
  /**
   * Notifica al modal padre el ancho de escritorio al abrir el detalle.
   */
  onDetalleAvanceChange?: (abierto: boolean) => void;
  onCerrar?: () => void;
}) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Estado que indica si el detalle de avance está abierto.
   */
  const { detalleAbierto, setDetalleAbierto } = useDetalleAvanceToggle(onDetalleAvanceChange);

  /**
   * Estado que indica si el modal de suscripción TER está abierto.
   */
  const [modalSuscripcionAbierto, setModalSuscripcionAbierto] = useState(false);

  /**
   * Indica si el cliente ya está suscrito al TER.
   */
  const clienteSuscrito = useSelector((state: RootState) => state.desconexion.clienteSuscritoTer);

  /**
   * Correo de la suscripción TER exitosa.
   */
  const correoSuscripcion = useSelector((state: RootState) => state.desconexion.correoSuscripcionTer);

  /**
   * Datos de vista del dashboard a partir de la respuesta de tracking.
   */
  const dashboard = useMemo(() => mapApiResponseToDashboardData(data, numeroOrden), [data, numeroOrden]);

  const sinMotivo = datoNoValido(dashboard.motivo_corte);
  const terLabels = buildTerLabels(t);
  const terDisplay = formatTerDisplay(data.fechaFinCorte, terLabels);
  const footerTexto = buildFooterTexto(data.fechaActualizacion, t);

  const secundario = data.estadoActualSecundario;
  /**
   * El corte concluido se pinta en verde solo si el estado secundario es normalizado.
   */
  const esVerde = secundario === TipoEstadoActualSecundarioTrackingCorteEnum.NORMALIZADO;

  /**
   * Índice del paso activo según el estado secundario del corte.
   */
  const pasoActual: number = (() => {
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.NORMALIZADO) {
      return INDICE_REPOSICION;
    }
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO) {
      return INDICE_BRIGADA_TERRENO;
    }
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_ASIGNADA) {
      return INDICE_BRIGADA_ASIGNADA;
    }
    /**
     * Confirmado u otro estado deja activo el corte confirmado.
     */
    return INDICE_CORTE_CONFIRMADO;
  })();

  /**
   * El corte concluido usa cuatro nodos. Dos etapas solo si no hay brigada y sigue en curso.
   */
  const usa4Etapas =
    dashboard.hasBrigadaAsignada ||
    esVerde ||
    Boolean(data.fechaArriboBrigada) ||
    secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_ASIGNADA ||
    secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO;
  const totalPasos = usa4Etapas ? CANTIDAD_ETAPAS_CON_BRIGADA : CANTIDAD_ETAPAS_SIN_BRIGADA;

  let pasoActivoUi = pasoActual;
  if (!usa4Etapas) {
    if (esVerde) {
      pasoActivoUi = INDICE_BRIGADA_ASIGNADA;
    } else {
      pasoActivoUi = INDICE_CORTE_CONFIRMADO;
    }
  }

  /**
   * Determina si un paso está completado, activo o pendiente.
   * @param index posición del paso en el timeline
   * @returns estado visual del nodo
   */
  const stepKind = (index: number): StepKind => {
    if (esVerde) {
      if (index < totalPasos - 1) {
        return ESTADO_NODO_HECHO;
      }
      return ESTADO_NODO_ACTIVO;
    }
    if (index < pasoActivoUi) {
      return ESTADO_NODO_HECHO;
    }
    if (index === pasoActivoUi) {
      return ESTADO_NODO_ACTIVO;
    }
    return ESTADO_NODO_PENDIENTE;
  };

  /**
   * El corte normalizado deja la barra de avance completa.
   */
  let progressPct = INDICE_CORTE_CONFIRMADO;
  if (esVerde) {
    progressPct = PROGRESO_COMPLETO;
  } else if (totalPasos > 1) {
    progressPct = (Math.min(pasoActivoUi, totalPasos - 1) / (totalPasos - 1)) * 100;
  }

  const iconos4 = [flashSlash, truck, repair, clock];
  const iconos2 = [flashSlash, clock];
  const iconos = usa4Etapas ? iconos4 : iconos2;

  const mensajesEstado: Record<number, { horaKey: keyof typeof dashboard.hora_etapa; descKey: string }> = {
    [INDICE_CORTE_CONFIRMADO]: {
      horaKey: 'corteConfirmado',
      descKey: 'desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.corteConfirmado',
    },
    [INDICE_BRIGADA_ASIGNADA]: {
      horaKey: 'asignacionBrigada',
      descKey: 'desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.asignacionBrigada',
    },
    [INDICE_BRIGADA_TERRENO]: {
      horaKey: 'brigadaEnTerreno',
      descKey: 'desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.brigadaEnTerreno',
    },
    [INDICE_REPOSICION]: {
      horaKey: 'reposicionEstimada',
      descKey: 'desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.reposicionEstimada',
    },
  };

  const mensajeIndex = usa4Etapas ? pasoActivoUi : INDICE_CORTE_CONFIRMADO;
  const mensajePaso = mensajesEstado[mensajeIndex] || mensajesEstado[INDICE_CORTE_CONFIRMADO];

  let horaEstado = formatTimeOnly(dashboard.hora_etapa[mensajePaso.horaKey]);
  if (esVerde) {
    /**
     * En el corte normalizado se muestra la hora de reposición.
     */
    horaEstado = formatTimeOnly(data.fechaFinCorte) || formatTimeOnly(dashboard.hora_etapa.reposicionEstimada);
  }

  /**
   * Cierra el modal de suscripción cuando el cliente ya quedó suscrito.
   */
  useEffect(() => {
    if (clienteSuscrito) {
      setModalSuscripcionAbierto(false);
    }
  }, [clienteSuscrito]);

  /**
   * Abre el modal para suscribirse a cambios del TER.
   */
  const abrirNotificar = () => {
    setModalSuscripcionAbierto(true);
  };

  const { objetoGrafico, colores } = buildDatosGraficoClientes(data.porcentajeClientesAunSinServicio, data.porcentajeClientesRecuperados);

  const motivoTitulo = sinMotivo ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.sinMotivoTitulo') : dashboard.motivo_corte;

  let avanceTitulo = sinMotivo
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.tituloSinMotivo')
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.titulo');
  if (esVerde) {
    avanceTitulo = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.tituloConcluido');
  }

  let estadoDescTexto = sinMotivo
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.motivoPendiente')
    : t(mensajePaso.descKey);
  if (esVerde) {
    estadoDescTexto = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.reposicionGradual');
  }

  const mostrarChart = dashboard.mostrarGraficoClientes;
  const mostrarNotificar = !esVerde && notificacionTerActivo && !clienteSuscrito;
  const mostrarBannerSuscrito = !esVerde && notificacionTerActivo && clienteSuscrito;
  /**
   * El corte concluido no muestra la caja de TER ni la suscripción.
   */
  const mostrarTer = !esVerde;

  /**
   * Banner de confirmación cuando el cliente ya se suscribió al TER.
   * @returns bloque "Recibimos tu correo"
   */
  const renderBannerSuscrito = () => (
    <div className={styles.terBoxSuscrito} data-testid={TEST_ID_TER_SUSCRITO}>
      <img src={tickCircleExito} alt={CADENA_VACIA} className={styles.terSuscritoCheck} aria-hidden />
      <div className={styles.terSuscritoTextos}>
        <p className={styles.terSuscritoTitulo}>{t('desconexion:paginas.mapaCortes.notificacionTer.clienteSuscrito.titulo')}</p>
        <p className={styles.terSuscritoText}>
          {t('desconexion:paginas.mapaCortes.notificacionTer.clienteSuscrito.subTitulo')}
          {correoSuscripcion ? <span className={styles.terSuscritoEmail}>{correoSuscripcion}</span> : null}
        </p>
      </div>
    </div>
  );

  /**
   * Arma la caja de reposición estimada, suscripción o TER en evaluación.
   * @returns contenido de la caja TER
   */
  const renderTerBox = () => {
    if (mostrarBannerSuscrito && !dashboard.tieneTer) {
      return renderBannerSuscrito();
    }

    if (!dashboard.tieneTer) {
      return (
        <div className={styles.terBox} data-testid={TEST_ID_TER_VACIO}>
          <p className={styles.terEmptyText}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.sinTer')}</p>
          {mostrarNotificar && (
            <button type="button" className={styles.botonNotificar} onClick={abrirNotificar} data-testid={TEST_ID_BOTON_NOTIFICAR}>
              <img src={notificationBell} alt={CADENA_VACIA} className={styles.iconoNotificar} aria-hidden />
              {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.notificar')}
            </button>
          )}
        </div>
      );
    }

    if (mostrarBannerSuscrito) {
      return (
        <div className={styles.terBoxConSuscripcion} data-testid={TEST_ID_TER_BOX}>
          <div className={styles.terTextoWrap}>
            <p className={styles.terHora}>{terDisplay.principal}</p>
            <p className={styles.terSub}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.esReposicion')}</p>
          </div>
          {renderBannerSuscrito()}
        </div>
      );
    }

    return (
      <div className={styles.terBox} data-testid={TEST_ID_TER_BOX}>
        <div className={styles.terTextoWrap}>
          <p className={styles.terHora}>{terDisplay.principal}</p>
          <p className={styles.terSub}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.esReposicion')}</p>
        </div>
        {mostrarNotificar && (
          <button type="button" className={styles.botonNotificar} onClick={abrirNotificar} data-testid={TEST_ID_BOTON_NOTIFICAR}>
            <img src={notificationBell} alt={CADENA_VACIA} className={styles.iconoNotificar} aria-hidden />
            {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.notificar')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className={styles.root}
      data-testid={TEST_ID_DASHBOARD}
      data-estado={esVerde ? TipoEstadoVistaDashboardEnum.NORMALIZADO : TipoEstadoVistaDashboardEnum.EN_CURSO}
    >
      {/**
       * Reemplaza (no overlay): evita hueco blanco del alto del dashboard oculto
       */}
      {detalleAbierto ? (
        <DetalleAvance data={data} numeroOrden={numeroOrden} onBack={() => setDetalleAbierto(false)} onCerrar={onCerrar} />
      ) : (
        <>
          <div className={styles.scrollArea}>
            <div className={styles.stickyCabecera}>
              <div className={styles.header}>
                <div className={styles.headerLeft}>
                  <span className={styles.statusDot} aria-hidden />
                  <p className={styles.statusLabel}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.enCurso')}</p>
                </div>
              </div>
              {onCerrar ? <BotonCerrarDashboard onCerrar={onCerrar} /> : null}
            </div>

            <div className={styles.content}>
              <div className={styles.body}>
                <div className={`${styles.leftCol} ${!mostrarChart ? styles.leftColFull : CADENA_VACIA}`}>
                  <h2 className={styles.motivo} data-testid={TEST_ID_MOTIVO}>
                    {motivoTitulo}
                  </h2>

                  {mostrarTer && renderTerBox()}

                  <div className={`${styles.avanceBox} ${esVerde ? styles.avanceBoxVerde : CADENA_VACIA}`} data-testid={TEST_ID_AVANCE}>
                    <p className={`${styles.avanceTitulo} ${esVerde ? styles.avanceTituloVerde : CADENA_VACIA}`}>{avanceTitulo}</p>

                    <div
                      className={`${styles.timelineRow} ${!usa4Etapas ? styles.timelineRowCorto : CADENA_VACIA}`}
                      data-testid={usa4Etapas ? TEST_ID_TIMELINE_CUATRO : TEST_ID_TIMELINE_DOS}
                    >
                      <div className={`${styles.timelineLineTrack} ${!usa4Etapas ? styles.timelineLineTrackCorto : CADENA_VACIA}`} />
                      <div
                        className={`${styles.timelineLineProgress} ${esVerde ? styles.timelineLineProgressGreen : CADENA_VACIA} ${
                          !usa4Etapas ? styles.timelineLineProgressCorto : CADENA_VACIA
                        }`}
                        style={{ [VARIABLE_ANCHO_PROGRESO]: anchoBarraProgreso(progressPct) } as CSSProperties}
                      />
                      {iconos.map((icono, index) => {
                        const kind = stepKind(index);
                        if (kind === ESTADO_NODO_HECHO) {
                          return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={`step-${index}`} className={styles.stepNode}>
                              <span className={`${styles.stepDone} ${esVerde ? styles.stepDoneGreen : CADENA_VACIA}`} />
                            </div>
                          );
                        }
                        const activeClass = esVerde ? styles.stepActiveGreen : styles.stepActive;
                        const nodeClass = kind === ESTADO_NODO_ACTIVO ? activeClass : styles.stepPending;
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={`step-${index}`} className={`${styles.stepNode} ${nodeClass}`}>
                            <img src={icono} alt={CADENA_VACIA} className={styles.stepIcon} />
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.estadoActualWrap}>
                      {!datoNoValido(horaEstado) && <p className={styles.estadoHora}>{`${horaEstado}${SUFIJO_HORAS}`}</p>}
                      <p className={styles.estadoDesc}>{estadoDescTexto}</p>
                      <button
                        type="button"
                        className={styles.linkDetalle}
                        onClick={() => setDetalleAbierto(true)}
                        data-testid={TEST_ID_TOGGLE_DETALLE}
                      >
                        {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.verDetalle')}
                        <img src={chevronRight} alt={CADENA_VACIA} className={styles.iconoLinkDetalle} aria-hidden />
                      </button>
                    </div>
                  </div>

                  {esVerde && (
                    <OutageReportarBox
                      textoInfo={t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.reportarInfo')}
                      testIdBox={TEST_ID_REPORTAR}
                      testIdBoton={TEST_ID_BOTON_REPORTAR_DASHBOARD}
                    />
                  )}
                </div>

                {mostrarChart && (
                  <OutageClientesChart
                    clientesSin={dashboard.clientes_sin_servicio}
                    clientesRec={dashboard.clientes_recuperados}
                    totalAfectados={dashboard.total_afectados}
                    objetoGrafico={objetoGrafico}
                    colores={colores}
                    testId={TEST_ID_CHART}
                  />
                )}
              </div>
            </div>
          </div>

          <div className={styles.footer} data-testid={TEST_ID_FOOTER}>
            <p className={styles.footerText}>{footerTexto}</p>
          </div>
        </>
      )}

      {notificacionTerActivo && !esVerde && (
        <ModalSuscripcionTer open={modalSuscripcionAbierto} numeroCaso={numeroOrden} onClose={() => setModalSuscripcionAbierto(false)} />
      )}
    </div>
  );
};

OutageDashboard.defaultProps = {
  onDetalleAvanceChange: undefined,
  onCerrar: undefined,
};

export default OutageDashboard;
